import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Stack, Columns, Column, IconChevron, HiddenVisually, Text } from "braid-design-system";
import { useMemo } from "react";
import { DateButtonWrapper } from "../DateButton/DateButtonWrapper.mjs";
import { translations } from "../../../components/BaseCalendar/.vocab/index.mjs";
import { root, nav } from "./BaseCalendar.css.mjs";
function addDays(d, n) {
  return new Date(new Date(d).setUTCDate(d.getUTCDate() + n));
}
function addMonths(d, m) {
  d.setUTCDate(1);
  return new Date(new Date(d).setUTCMonth(d.getUTCMonth() + m));
}
function isoDateFromDate(d) {
  return d.toISOString().split("T")[0];
}
function setDayOfMonth(d, dayOfMonth) {
  return new Date(new Date(d).setUTCDate(dayOfMonth));
}
function utcDateFromLocalDate(d) {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
}
function getRowsFromCurrentUtcDate({
  offset,
  currentUtcDate
}) {
  const daysInMonth = new Date(currentUtcDate).getUTCDate();
  return Math.ceil((daysInMonth + offset) / 7);
}
const BaseCalendar = ({
  id = "DatePickerBaseCalendar",
  deactivateStartDate = false,
  deactivateEndDate = false,
  currentLocalDate,
  maxDate: maxDateStr,
  minDate: minDateStr,
  value,
  customRootClass,
  isCompact = false,
  onDateButtonClicked,
  onMonthClicked
}) => {
  const { t } = useTranslations(translations);
  const currentUtcDate = useMemo(
    () => utcDateFromLocalDate(currentLocalDate),
    [currentLocalDate]
  );
  const currentMonth = useMemo(
    () => new Date(currentUtcDate).getUTCMonth(),
    [currentUtcDate]
  );
  const currentYear = useMemo(
    () => new Date(currentUtcDate).getUTCFullYear(),
    [currentUtcDate]
  );
  const startDay = useMemo(
    () => setDayOfMonth(new Date(currentUtcDate), 1),
    [currentUtcDate]
  );
  const offset = useMemo(() => startDay.getUTCDay(), [startDay]);
  const rows = useMemo(
    () => getRowsFromCurrentUtcDate({ offset, currentUtcDate }),
    [offset, currentUtcDate]
  );
  const columns = 7;
  const maxDate = new Date(maxDateStr);
  const minDate = new Date(minDateStr);
  const maxDateMonth = useMemo(
    () => new Date(maxDateStr).getUTCMonth(),
    [maxDateStr]
  );
  const minDateMonth = useMemo(
    () => new Date(minDateStr).getUTCMonth(),
    [minDateStr]
  );
  const maxDateYear = useMemo(
    () => new Date(maxDateStr).getUTCFullYear(),
    [maxDateStr]
  );
  const minDateYear = useMemo(
    () => new Date(minDateStr).getUTCFullYear(),
    [minDateStr]
  );
  const disablePreviousMonthButton = currentMonth <= minDateMonth && currentYear === minDateYear;
  const disableNextMonthButton = currentMonth >= maxDateMonth && currentYear === maxDateYear;
  const previousMonthStr = isoDateFromDate(
    addMonths(new Date(currentUtcDate), -1)
  );
  const nextMonthStr = isoDateFromDate(addMonths(new Date(currentUtcDate), 1));
  const onDateButtonClickedEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const target = e.target.closest(
      "button"
    );
    const newValue = target.dataset.value;
    if (newValue) {
      onDateButtonClicked(newValue);
    }
  };
  const onMonthClickedEvent = (e) => {
    e.stopPropagation();
    const target = e.target.closest(
      "button"
    );
    const newMonth = target.dataset.month;
    if (newMonth) {
      onMonthClicked(newMonth);
    }
  };
  return /* @__PURE__ */ jsx(Box, { id, className: [root, customRootClass], children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsxs(Columns, { space: "medium", alignY: "center", children: [
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(
        Box,
        {
          className: nav,
          component: "button",
          cursor: disablePreviousMonthButton ? "default" : "pointer",
          "data-month": previousMonthStr,
          data: { testid: "date-prev-month-trigger" },
          disabled: disablePreviousMonthButton,
          display: "block",
          outline: "none",
          position: "relative",
          transform: { active: "touchable" },
          transition: "touchable",
          type: "button",
          onClick: onMonthClickedEvent,
          children: [
            /* @__PURE__ */ jsx(IconChevron, { direction: "left", tone: "secondary" }),
            /* @__PURE__ */ jsx(HiddenVisually, { children: "Previous month" })
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { align: "center", children: `${t(`month-${currentMonth}`)} ${new Date(
        currentUtcDate
      ).getUTCFullYear()}` }) }),
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(
        Box,
        {
          className: nav,
          component: "button",
          cursor: disableNextMonthButton ? "default" : "pointer",
          "data-month": nextMonthStr,
          data: { testid: "date-next-month-trigger" },
          disabled: disableNextMonthButton,
          display: "block",
          outline: "none",
          position: "relative",
          transform: { active: "touchable" },
          transition: "touchable",
          type: "button",
          onClick: onMonthClickedEvent,
          children: [
            /* @__PURE__ */ jsx(IconChevron, { direction: "right", tone: "secondary" }),
            /* @__PURE__ */ jsx(HiddenVisually, { children: "Next month" })
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx(Columns, { space: "none", children: Array(columns).fill(0).map((_, column) => /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
      Text,
      {
        align: "center",
        tone: "secondary",
        size: isCompact ? "xsmall" : "small",
        children: t(`day-${column}`)
      }
    ) }, `date-picker-header-${column}`)) }),
    /* @__PURE__ */ jsx(Stack, { space: "none", children: Array(rows).fill(0).map((_, row) => /* @__PURE__ */ jsx(
      Columns,
      {
        space: "none",
        children: Array(columns).fill(0).map((__, column) => {
          const k = row * 7 + column - offset;
          const day = addDays(startDay, k);
          const isoDate = isoDateFromDate(day);
          if (currentMonth !== day.getUTCMonth()) {
            return /* @__PURE__ */ jsx(
              Column,
              {
                children: /* @__PURE__ */ jsx("span", {})
              },
              `date-picker-row-${currentMonth}-${row}-${column}`
            );
          }
          return /* @__PURE__ */ jsx(
            Column,
            {
              children: /* @__PURE__ */ jsx(
                DateButtonWrapper,
                {
                  deactivateEndDate,
                  deactivateStartDate,
                  isActive: isoDate === value.startDate || isoDate === value.endDate,
                  isCompact,
                  isDisabled: day > maxDate || day < minDate,
                  isEnd: isoDate === value.endDate,
                  isInRange: day > new Date(value.startDate) && day < new Date(value.endDate),
                  isStart: isoDate === value.startDate,
                  value: isoDate,
                  onClick: onDateButtonClickedEvent,
                  children: day.getUTCDate()
                }
              )
            },
            `date-picker-row-${currentMonth}-${row}-${column}`
          );
        })
      },
      `date-picker-row-${currentMonth}-${row}`
    )) })
  ] }) });
};
export {
  BaseCalendar
};
